import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AreaChart } from "../components/ui/AreaChart";
import { BarChart } from "../components/ui/BarChart";
import { Callout } from "../components/ui/Callout";
import { Card } from "../components/ui/Card";
import { api } from "../lib/api";
import type { RecentFood, StatsResponse } from "../lib/types";
import { fmtNumber, fmtPercent, fmtRelative } from "../lib/format";

/**
 * Insights — admin dashboard.
 *
 * Layout ladder (console.anthropic.com aesthetic):
 *   1. Page header
 *   2. 4 large KPI numbers (catalog / hit rate / pending / searches)
 *   3. Cumulative-foods area chart (full width)
 *   4. Two-up: source mix + top searched
 *   5. Two-up: ingredient depth + moderation breakdown
 *   6. Recent foods list
 *
 * Every chart renders in a single muted gray. Source identity comes
 * from labels, not color — keeps the page reading as one coherent
 * surface instead of a chart circus. Section labels are 11px uppercase
 * letterspaced gray to match the KPI labels above.
 */

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const RANGES: Array<{ label: string; days: number }> = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

export function StatsPage() {
  const [days, setDays] = useState(30);
  const { data, isLoading, error } = useQuery<StatsResponse>({
    queryKey: ["stats", days],
    queryFn: () => api<StatsResponse>(`/v1/admin/stats?days=${days}`),
  });

  if (isLoading) {
    return <p className="text-sm text-gray-500 dark:text-gray-500">Loading…</p>;
  }

  if (error || !data) {
    return (
      <Callout title="Failed to load" variant="error">
        {(error as Error)?.message ?? "unknown"}
      </Callout>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-2 py-6 space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
            Insights
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-500">
            Endpoint usage, cache behavior, and source quality for the app.
          </p>
        </div>
        <RangePicker value={days} onChange={setDays} />
      </header>
      <EndpointOverview data={data} />
      <EndpointUsageBlock data={data} />
      <TrendBlock recent={data.recent} totalFoods={data.summary.totalFoods} />
      <ChartBand data={data} />
      <RecentBlock rows={data.recent.slice(0, 8)} />
    </div>
  );
}

// ── Section header (11px uppercase tracking) ─────────────────────────────

function SectionHeader({ label, meta }: { label: string; meta?: string }) {
  return (
    <div className="mb-4 flex items-baseline justify-between">
      <h2 className="text-[11px] font-medium uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400">
        {label}
      </h2>
      {meta && (
        <span className="text-xs text-gray-400 dark:text-gray-500">{meta}</span>
      )}
    </div>
  );
}

function RangePicker({
  value,
  onChange,
}: {
  value: number;
  onChange: (days: number) => void;
}) {
  return (
    <div
      className="inline-flex rounded-md border border-gray-200 bg-white p-0.5 text-xs dark:border-gray-800 dark:bg-gray-950"
      role="tablist"
      aria-label="Date range"
    >
      {RANGES.map((r) => (
        <button
          key={r.days}
          type="button"
          role="tab"
          aria-selected={r.days === value}
          onClick={() => onChange(r.days)}
          className={
            r.days === value
              ? "rounded-md bg-gray-900 px-3 py-1 text-white dark:bg-gray-100 dark:text-gray-900"
              : "rounded-md px-3 py-1 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-900"
          }
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}

function EndpointOverview({ data }: { data: StatsResponse }) {
  const usage = data.endpointUsage;
  const foodSearch = usage.totals.find((r) => r.feature === "food_search");
  const foodAutocomplete = usage.totals.find(
    (r) => r.feature === "food_autocomplete",
  );
  const barcode = usage.totals.find((r) => r.feature === "barcode_lookup");
  const scan = usage.totals.find((r) => r.feature === "image_scan");

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <MetricCard
        label="Food search"
        value={fmtNumber(
          (foodSearch?.count ?? 0) + (foodAutocomplete?.count ?? 0),
        )}
        sub={`${fmtNumber(data.featureUsage.search)} lifetime search requests`}
      />
      <MetricCard
        label="Food lookup fail rate"
        value={
          data.foodLookupHealth.fail_rate != null
            ? fmtPercent(data.foodLookupHealth.fail_rate)
            : "—"
        }
        sub={`${fmtNumber(data.foodLookupHealth.missed)} misses of ${fmtNumber(data.foodLookupHealth.total)} lookups`}
        tone={
          data.foodLookupHealth.fail_rate != null &&
          data.foodLookupHealth.fail_rate > 0.2
            ? "warn"
            : undefined
        }
      />
      <MetricCard
        label="Barcode usage"
        value={fmtNumber(barcode?.count ?? 0)}
        sub={
          barcode?.failure_rate != null
            ? `${fmtPercent(barcode.failure_rate)} not found/error`
            : `last ${usage.range.days} days after analytics deploy`
        }
      />
      <MetricCard
        label="Image scans"
        value={fmtNumber(scan?.count ?? 0)}
        sub={
          scan?.failure_rate != null
            ? `${fmtPercent(scan.failure_rate)} model/service errors`
            : `last ${usage.range.days} days after analytics deploy`
        }
      />
    </div>
  );
}

function MetricCard({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "warn";
}) {
  return (
    <Card className="min-h-[112px]">
      <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400">
        {label}
      </span>
      <p
        className={`mt-2 text-2xl font-semibold tabular-nums leading-none ${
          tone === "warn"
            ? "text-amber-600 dark:text-amber-400"
            : "text-gray-950 dark:text-gray-50"
        }`}
      >
        {value}
      </p>
      {sub && (
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{sub}</p>
      )}
    </Card>
  );
}

function EndpointUsageBlock({ data }: { data: StatsResponse }) {
  const usage = data.endpointUsage;
  const daily = usage.by_day.map((d) => ({
    day: d.day,
    "Food search": d.food_search + d.food_autocomplete,
    "Food lookup": d.food_lookup,
    Barcode: d.barcode_lookup,
    Scan: d.image_scan,
  }));
  const sourceItems = usage.by_source
    .filter((row) => row.name !== "none")
    .slice(0, 8)
    .map((row) => ({
      name: `${shortFeature(row.feature)} · ${row.name}`,
      value: row.count,
    }));
  const cacheItems = usage.by_cache
    .filter((row) => row.name !== "none")
    .slice(0, 8)
    .map((row) => ({
      name: `${shortFeature(row.feature)} · ${row.name}`,
      value: row.count,
    }));

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <Card className="lg:col-span-2">
        <SectionHeader
          label="Endpoint usage"
          meta={`last ${usage.range.days} days`}
        />
        {usage.totals.every((row) => row.count === 0) ? (
          <EmptyChart label="No endpoint usage recorded yet." />
        ) : (
          <AreaChart
            data={daily}
            index="day"
            categories={["Food search", "Food lookup", "Barcode", "Scan"]}
            colors={["gray", "cyan", "emerald", "amber"]}
            valueFormatter={fmtNumber}
            showLegend
            className="h-64"
          />
        )}
      </Card>

      <div className="space-y-6">
        <Card>
          <SectionHeader label="Usage source" />
          {sourceItems.length === 0 ? (
            <EmptyChart label="No source data yet." />
          ) : (
            <MonoBarList data={sourceItems} valueFormatter={fmtNumber} />
          )}
        </Card>
        <Card>
          <SectionHeader label="Cache state" />
          {cacheItems.length === 0 ? (
            <EmptyChart label="No cache data yet." />
          ) : (
            <MonoBarList data={cacheItems} valueFormatter={fmtNumber} />
          )}
        </Card>
      </div>
    </div>
  );
}

// ── Cumulative foods trend (full width) ──────────────────────────────────

function TrendBlock({
  recent,
  totalFoods,
}: {
  recent: RecentFood[];
  totalFoods: number;
}) {
  const data = cumulativeGrowth(recent, totalFoods);
  return (
    <Card>
      <SectionHeader
        label="Cumulative foods"
        meta={`${data.length} ${data.length === 1 ? "day" : "days"}`}
      />
      {/* h-52 instead of h-64: a flatter aspect feels right when the data
          is monotonically increasing. The chart was visually awkward at
          h-64 because the curve was forced into a near-horizontal line at
          the top of a tall box. */}
      <AreaChart
        data={data}
        index="day"
        categories={["Total"]}
        colors={["gray"]}
        valueFormatter={fmtNumber}
        showLegend={false}
        startEndOnly
        className="h-52"
      />
    </Card>
  );
}

// ── Chart band: 4 charts in 2 rows ───────────────────────────────────────

function ChartBand({ data }: { data: StatsResponse }) {
  const sourceItems = data.bySource.map((s) => ({
    name: s.source,
    value: s.count,
  }));

  // Service usage: lifetime totals per iOS feature, ranked descending so
  // the highest-traffic bucket sits at the top. Labels match what the iOS
  // app actually calls (search/lookup/suggest/barcode/scan/read/report)
  // rather than the underlying activity-bucket column names.
  const featureItems = [
    { name: "Search", value: data.featureUsage.search },
    { name: "Lookup", value: data.featureUsage.lookup },
    { name: "Suggest", value: data.featureUsage.suggest },
    { name: "Barcode", value: data.featureUsage.barcode_lookup },
    { name: "Image scan", value: data.featureUsage.image_scan },
    { name: "Reads", value: data.featureUsage.read },
    { name: "Reports", value: data.featureUsage.report },
  ]
    .filter((b) => b.value > 0)
    .sort((a, b) => b.value - a.value);

  const topSearchedItems = data.topSearched.slice(0, 8).map((row) => ({
    name: row.name,
    Searches: row.search_count,
  }));

  const depthItems = ingredientHistogram(data.recent);

  const moderationItems = [
    { status: "Pending", Count: data.summary.pendingReports },
    { status: "Approved", Count: data.summary.approvedReports },
    { status: "Rejected", Count: data.summary.rejectedReports },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <SectionHeader
          label="Service usage"
          meta={`${fmtNumber(data.featureUsage.total)} authed requests`}
        />
        {data.featureUsage.total === 0 ? (
          <EmptyChart label="No activity yet." />
        ) : (
          <MonoBarList data={featureItems} valueFormatter={fmtNumber} />
        )}
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <SectionHeader
            label="Source mix"
            meta={`${sourceItems.length} sources`}
          />
          {sourceItems.length === 0 ? (
            <EmptyChart label="No foods yet." />
          ) : (
            <MonoBarList data={sourceItems} valueFormatter={fmtNumber} />
          )}
        </Card>

        <Card>
          <SectionHeader
            label="Top searched"
            meta={`${topSearchedItems.length} foods`}
          />
          {topSearchedItems.length === 0 ? (
            <EmptyChart label="No searches yet." />
          ) : (
            <BarChart
              data={topSearchedItems}
              index="name"
              categories={["Searches"]}
              colors={["gray"]}
              valueFormatter={fmtNumber}
              yAxisWidth={60}
              showLegend={false}
              layout="vertical"
              className="h-56"
            />
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <SectionHeader label="Ingredient depth" meta="recent rows" />
          {data.recent.length === 0 ? (
            <EmptyChart label="No foods yet." />
          ) : (
            <BarChart
              data={depthItems}
              index="bucket"
              categories={["Foods"]}
              colors={["gray"]}
              valueFormatter={fmtNumber}
              yAxisWidth={32}
              showLegend={false}
              className="h-44"
            />
          )}
        </Card>

        <Card>
          <SectionHeader
            label="Moderation"
            meta={`${fmtNumber(data.summary.totalReports)} total`}
          />
          {data.summary.totalReports === 0 ? (
            <EmptyChart label="No reports yet." />
          ) : (
            <BarChart
              data={moderationItems}
              index="status"
              categories={["Count"]}
              colors={["gray"]}
              valueFormatter={fmtNumber}
              yAxisWidth={32}
              showLegend={false}
              className="h-44"
            />
          )}
        </Card>
      </div>
    </div>
  );
}

function EmptyChart({ label }: { label: string }) {
  return (
    <div className="flex h-32 items-center justify-center text-sm text-gray-400 dark:text-gray-500">
      {label}
    </div>
  );
}

// ── Monochrome horizontal bar list ────────────────────────────────────────
// The shared BarList primitive hardcodes blue bars; this local copy stays
// in the page's gray palette without touching the shared component (which
// is also used by the Barcodes + Scans pages).

function MonoBarList({
  data,
  valueFormatter,
}: {
  data: { name: string; value: number }[];
  valueFormatter: (n: number) => string;
}) {
  const max = data.reduce((m, d) => (d.value > m ? d.value : m), 0);
  if (max === 0) {
    return <EmptyChart label="No data yet." />;
  }
  return (
    <ul className="space-y-1.5">
      {data.map((d) => {
        const pct = Math.max(2, Math.round((d.value / max) * 100));
        return (
          <li key={d.name} className="relative">
            <div
              aria-hidden
              className="absolute inset-y-0 left-0 rounded-sm bg-stone-200 dark:bg-white/10"
              style={{ width: `${pct}%` }}
            />
            <div className="relative flex items-center justify-between gap-3 px-2 py-1.5 text-sm">
              <span className="truncate text-gray-900 dark:text-gray-50">
                {d.name}
              </span>
              <span className="shrink-0 tabular-nums text-gray-500 dark:text-gray-400">
                {valueFormatter(d.value)}
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// ── Recent foods (mailbox-style list) ────────────────────────────────────

function RecentBlock({ rows }: { rows: RecentFood[] }) {
  return (
    <section className="py-10">
      <SectionHeader
        label="Recent foods"
        meta={`Last ${rows.length} cache fills`}
      />
      {rows.length === 0 ? (
        <p className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
          No foods yet.
        </p>
      ) : (
        <ul className="divide-y divide-stone-200 dark:divide-white/10">
          {rows.map((row) => (
            <li
              key={row.id}
              className="grid grid-cols-[1fr_auto_auto_auto] items-baseline gap-6 py-4 text-sm"
            >
              <p className="min-w-0 truncate text-gray-950 dark:text-gray-50">
                {row.name}
              </p>
              <span className="font-mono text-xs uppercase tracking-wider text-gray-500 dark:text-gray-500">
                {row.source}
              </span>
              <span className="text-xs tabular-nums text-gray-500 dark:text-gray-500">
                {row.ingredient_count} ing
              </span>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {fmtRelative(row.created_at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────

function cumulativeGrowth(
  recent: RecentFood[],
  totalFoods: number,
): { day: string; Total: number }[] {
  if (recent.length === 0) {
    const today = Math.floor(Date.now() / ONE_DAY_MS);
    return [
      { day: shortDate((today - 1) * ONE_DAY_MS), Total: totalFoods },
      { day: shortDate(today * ONE_DAY_MS), Total: totalFoods },
    ];
  }
  const sorted = [...recent].sort((a, b) => a.created_at - b.created_at);
  const startMs = sorted[0]!.created_at;
  const endMs = Date.now();
  const startDay = Math.floor(startMs / ONE_DAY_MS);
  const endDay = Math.floor(endMs / ONE_DAY_MS);
  const baseline = Math.max(0, totalFoods - sorted.length);

  const perDay = new Map<number, number>();
  for (const row of sorted) {
    const day = Math.floor(row.created_at / ONE_DAY_MS) - startDay;
    perDay.set(day, (perDay.get(day) ?? 0) + 1);
  }

  const out: { day: string; Total: number }[] = [];
  let running = baseline;
  const dayCount = endDay - startDay + 1;
  const cap = Math.min(dayCount, 30);
  for (let d = 0; d < cap; d++) {
    running += perDay.get(d) ?? 0;
    const ts = (startDay + d) * ONE_DAY_MS;
    out.push({ day: shortDate(ts), Total: running });
  }
  if (out.length === 1) {
    return [
      { day: shortDate((startDay - 1) * ONE_DAY_MS), Total: baseline },
      ...out,
    ];
  }
  return out;
}

function ingredientHistogram(
  recent: RecentFood[],
): { bucket: string; Foods: number }[] {
  const buckets: { bucket: string; min: number; max: number }[] = [
    { bucket: "1–3", min: 1, max: 3 },
    { bucket: "4–6", min: 4, max: 6 },
    { bucket: "7–9", min: 7, max: 9 },
    { bucket: "10+", min: 10, max: Infinity },
  ];
  return buckets.map(({ bucket, min, max }) => ({
    bucket,
    Foods: recent.filter(
      (r) => r.ingredient_count >= min && r.ingredient_count <= max,
    ).length,
  }));
}

function shortFeature(feature: string): string {
  switch (feature) {
    case "food_autocomplete":
      return "Autocomplete";
    case "food_search":
      return "Search";
    case "food_lookup":
      return "Lookup";
    case "barcode_lookup":
      return "Barcode";
    case "image_scan":
      return "Scan";
    default:
      return feature;
  }
}

function shortDate(ms: number): string {
  return new Date(ms).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}
